import type React from "react";
import { Button } from "./Button";
import { FaTrash } from "react-icons/fa";
import { Upload, CheckCircle, AlertCircle } from "lucide-react";
import FileUploadCard from "./FileUploadCard";
import CompletedMediaGrid from "./CompletedMediaGrid";
import { type UploadQueueState, UploadStatus } from "../listings/types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FaCircleNotch } from "react-icons/fa6";
import { GoDotFill } from "react-icons/go";

interface UploadQueueProps {
  queueState: UploadQueueState;
  onRemoveFile: (fileId: string) => void;
  onRetryFile: (fileId: string) => void;
  onCancelFile: (fileId: string) => void;
  onClearCompleted: () => void;
  showCompactView?: boolean;
  onShowProcessingOverlay?: () => void;
}

const UploadQueue: React.FC<UploadQueueProps> = ({
  queueState,
  onRemoveFile,
  onRetryFile,
  onCancelFile,
  onClearCompleted,
  showCompactView = false,
  onShowProcessingOverlay,
}) => {
  const isMobile = useIsMobile(700);

  const pendingFiles = queueState.files.filter(
    (f) => f.status === UploadStatus.PENDING
  );
  const activeFiles = queueState.files.filter((f) =>
    [
      UploadStatus.VALIDATING,
      UploadStatus.GETTING_SIGNED_URL,
      UploadStatus.UPLOADING,
      UploadStatus.PROCESSING,
      UploadStatus.PERSISTING,
    ].includes(f.status)
  );
  const completedFiles = queueState.files.filter(
    (f) => f.status === UploadStatus.COMPLETED
  );
  const failedFiles = queueState.files.filter(
    (f) => f.status === UploadStatus.FAILED
  );
  const cancelledFiles = queueState.files.filter(
    (f) => f.status === UploadStatus.CANCELLED
  );

  const hasCompleted =
    completedFiles.length > 0 ||
    failedFiles.length > 0 ||
    cancelledFiles.length > 0;

  if (queueState.files.length === 0) {
    return null;
  }

  return (
    <div className="mt-6">
      {/* Queue Header */}
      <div className={`flex flex-col mb-4 ${isMobile ? "gap-2" : ""}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-primary flex items-center">
            <Upload className="h-5 w-5 mr-2" />
            Upload Queue
          </h3>

          {/* Actions */}
          {hasCompleted && (
            <Button size="sm" onClick={onClearCompleted}>
              <FaTrash className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {/* Status Summary */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm">
          {(queueState.activeImageUploads > 0 ||
            queueState.activeVideoUploads > 0) && (
            <span className="flex items-center text-secondary">
              <FaCircleNotch className="h-4 w-4 mr-1 animate-spin" />
              {queueState.activeImageUploads > 0 &&
                `${queueState.activeImageUploads} image${
                  queueState.activeImageUploads > 1 ? "s" : ""
                }`}
              {queueState.activeImageUploads > 0 &&
                queueState.activeVideoUploads > 0 &&
                ", "}
              {queueState.activeVideoUploads > 0 &&
                `${queueState.activeVideoUploads} video${
                  queueState.activeVideoUploads > 1 ? "s" : ""
                }`}
            </span>
          )}
          {completedFiles.length > 0 && (
            <span className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-1" />
              {completedFiles.length} completed
            </span>
          )}
          {failedFiles.length > 0 && (
            <span className="flex items-center text-red-600">
              <AlertCircle className="h-4 w-4 mr-1" />
              {failedFiles.length} failed
            </span>
          )}
          {pendingFiles.length > 0 && (
            <span className="flex items-center text-gray-600">
              <GoDotFill className="h-4 w-4 mr-1" />
              {pendingFiles.length} waiting
            </span>
          )}
        </div>
      </div>

      {/* Overall Progress Bar */}
      {queueState.files.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Overall Progress</span>
            <span>{Math.round(queueState.totalProgress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-secondary h-2 rounded-full transition-all duration-500"
              style={{ width: `${queueState.totalProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* File Cards */}
      <div className={`space-y-3 ${isMobile ? "space-y-2" : ""}`}>
        {/* Active uploads first */}
        {activeFiles.map((fileState) => (
          <FileUploadCard
            key={fileState.id}
            fileState={fileState}
            onRemove={onRemoveFile}
            onRetry={onRetryFile}
            onCancel={onCancelFile}
            onShowProcessingOverlay={onShowProcessingOverlay}
          />
        ))}

        {/* Pending uploads */}
        {pendingFiles.map((fileState) => (
          <FileUploadCard
            key={fileState.id}
            fileState={fileState}
            onRemove={onRemoveFile}
            onRetry={onRetryFile}
            onCancel={onCancelFile}
            onShowProcessingOverlay={onShowProcessingOverlay}
          />
        ))}
      </div>

      {/* Completed/Failed uploads (show only in non-compact mode) */}
      {!showCompactView && hasCompleted && (
        <CompletedMediaGrid
          completedFiles={completedFiles}
          failedFiles={failedFiles}
          cancelledFiles={cancelledFiles}
          onRemove={onRemoveFile}
          onRetry={onRetryFile}
        />
      )}

      {/* Compact view summary for completed/failed */}
      {showCompactView && hasCompleted && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {completedFiles.length > 0 && (
                <span className="mr-4">
                  <CheckCircle className="inline h-4 w-4 mr-1 text-green-500" />
                  {completedFiles.length} completed
                </span>
              )}
              {failedFiles.length > 0 && (
                <span>
                  <AlertCircle className="inline h-4 w-4 mr-1 text-red-500" />
                  {failedFiles.length} failed
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearCompleted}
              className="text-xs"
            >
              Clear All
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadQueue;
